import { isDeepEqual } from '@attain-apps/data-utils'
import { TypeAsserter, TypeChecker } from '@attain-apps/type-guards'

import * as common from '../../../../../common'
import type { Expression } from '../../expression'
import * as expressionNode from '../../node'
import type { FnHelper } from '../helper'
import { Key } from '../key'
import * as fnNode from '../node'

import { CommonArgs, standardNodeCreator } from './utils'

/**
 * Compares the results of each of the provided expressions and returns true
 * only if all of the results are the same
 *
 * @param expressions The expressions to compare
 * @example
 * // Intrinsic values
 * { '=': [ 'true', 'true' ] }
 * // ->
 * true
 * // Objects
 * { '=': [ { value: 'true' }, { value: 'false' } ] }
 * // ->
 * false
 * // Lists
 * { '=': [ [ true ], [ true ] ] }
 * // ->
 * true
 */
export interface Equals<out T = any> {
  /** {@link Equals} */
  readonly [Key.Equals]: T extends boolean ? readonly Expression<any>[] : never
}
export namespace Equals {
  export const isEquals: TypeChecker<Equals> = TypeChecker.Factory.lazy(() =>
    TypeChecker.Factory.object<Equals>({
      [Key.Equals]: CommonArgs.isListOfExpressions,
    }),
  ).usingAlias('Fn.Equals')
  export const assertIsEquals: TypeAsserter<Equals> =
    TypeAsserter.Factory.from(isEquals)

  /**
   * @see {@link Node}
   */
  export class Node<T extends boolean = boolean> extends fnNode.Node<
    T,
    Key.Equals,
    | readonly fnNode.Node[]
    // There is no reason for us to include more than one value node, since we
    // can check if those are equal ahead of time. So if there is a value node
    // (or anything that is not a Fn), then it is only going to be the first
    // item in the list
    | readonly [
        valueOrFnNode: expressionNode.Node,
        ...fnNodes: readonly fnNode.Node[],
      ]
  > {
    /**
     * If there is a value being compared, this is it
     */
    public get valueNode(): expressionNode.ForValue | undefined {
      const potentialValueNode = this.arg[0]
      return expressionNode.ForValue.isForValue(potentialValueNode)
        ? potentialValueNode
        : undefined
    }
    /**
     * Everything that is not a value
     */
    public get fnNodes(): readonly Node[] {
      // NOTE: the tuple type used for arg should ensure that this casting is
      // correct
      return (this.valueNode ? this.arg.slice(1) : this.arg) as readonly Node[]
    }

    public readonly type = common.value.types.Intrinsic.Boolean

    constructor(arg: Node<T>['arg']) {
      super(Key.Equals, arg)
    }
  }
  export namespace Node {
    export const isNode: TypeChecker<Node> = TypeChecker.Factory.instance(Node)
    export const assertIsNode: TypeAsserter<Node> =
      TypeAsserter.Factory.from(isNode)
  }

  const expressionsInArgs: FnHelper<Equals>['expressionsInArgs'] = fn =>
    fn[Key.Equals]
  export const HELPER: FnHelper<Equals> = {
    createNode: standardNodeCreator.usingExpressionList({
      // If there are no items, then it is reasonable to say all items are
      // equal
      emptyValue: true,
      finalizeArgs: (args: Node<any>['arg'], stash) =>
        [
          // Adds the first value node, if it exists and there is also at least
          // one non-value node to compare to
          ...(args.length !== 0 && stash.value !== undefined
            ? [new expressionNode.ForValue(stash.value)]
            : []),
          ...args,
        ] as typeof args,
      getArgExpressions: expressionsInArgs,
      handleValue: (node, stash) => {
        // NOTE: we need to nullish coalesce to `null` so that we treat
        // null & undefined as equal
        const currentValue = node.value ?? null
        if (stash.value !== undefined) {
          // If we have a value from an earlier expression and this value is not
          // equal to that value, the whole expression is false
          if (!isDeepEqual(currentValue, stash.value)) {
            return { return: false }
          }
        } else {
          stash.value = currentValue
        }
        // If this is our first value or this value equals our first value, we
        // can simply move on as the first value will be inserted into the args
        // at the end
        return undefined
      },
      nodeClass: Node,
      // We compare everything to the first value we encounter
      setupStash: (): { value?: unknown } => ({}),
      // Equals is the same with 0 or 1 arguments
      treatSingleArgAs: 'empty',
    }),
    expressionsInArgs,
    typeChecker: isEquals,
  }
}
